import { useEffect, useState } from 'react';
import api, { fileUrl } from './api';

// Resolve an asset path: backend uploads -> API host; absolute URLs as-is;
// everything else (frontend /public assets) returned untouched.
export const resolveAsset = (p) => {
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  if (p.startsWith('/uploads')) return fileUrl(p);
  return p;
};

// Module-level cache + in-flight guard so the global site settings are fetched
// once and shared by the Navbar, Footer, and favicon — no matter how many
// components call the hook.
let cache = null;
let inflight = null;
const subscribers = new Set();

function setFavicon(settings) {
  if (!settings.favicon) return;
  let link = document.querySelector('link#app-favicon');
  if (!link) {
    link = document.createElement('link');
    link.id = 'app-favicon';
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = resolveAsset(settings.favicon);
}

// Upsert a <meta> tag by name or property.
function setMeta(attr, key, value) {
  if (!value) return;
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
}

// Map of site_settings colour fields -> CSS custom properties. Set as inline
// styles on <html> so they override the :root defaults declared in Home.jsx.
const THEME_VARS = {
  muted_text_color: '--muted',
  header_bg: '--header-bg',
  header_text_color: '--header-text',
  header_menu_color: '--header-menu',
  footer_bg: '--footer-bg',
  footer_text_color: '--footer-text',
  footer_menu_color: '--footer-menu',
  footer_heading_color: '--footer-heading',
};

function applyTheme(settings) {
  if (!settings || typeof document === 'undefined') return;
  const root = document.documentElement;
  Object.entries(THEME_VARS).forEach(([field, cssVar]) => {
    if (settings[field]) root.style.setProperty(cssVar, settings[field]);
  });
}

let analyticsApplied = false;

function injectScript({ src, async, inline, id }) {
  if (id && document.getElementById(id)) return;
  const s = document.createElement('script');
  if (id) s.id = id;
  if (src) { s.src = src; if (async) s.async = true; }
  if (inline) s.text = inline;
  document.head.appendChild(s);
}

// Inject arbitrary admin-provided markup, re-creating <script> nodes so they run.
function injectRawHtml(html, where) {
  if (!html) return;
  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  const target = where === 'body' ? document.body : document.head;
  if (!target) return;
  Array.from(wrap.childNodes).forEach((node) => {
    if (node.tagName === 'SCRIPT') {
      const s = document.createElement('script');
      Array.from(node.attributes).forEach((a) => s.setAttribute(a.name, a.value));
      s.text = node.textContent;
      target.appendChild(s);
    } else {
      target.appendChild(node.cloneNode(true));
    }
  });
}

// Google Tag Manager / GA4 / Meta Pixel + custom scripts.
function applyAnalytics(s) {
  if (!s || analyticsApplied || typeof document === 'undefined') return;
  analyticsApplied = true;

  if (s.ga4_id) {
    injectScript({ src: `https://www.googletagmanager.com/gtag/js?id=${s.ga4_id}`, async: true, id: 'ga4-src' });
    injectScript({ id: 'ga4-init', inline:
      `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${s.ga4_id}');` });
  }

  if (s.gtm_id) {
    injectScript({ id: 'gtm-src', inline:
      `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${s.gtm_id}');` });
    if (document.body && !document.getElementById('gtm-noscript')) {
      const ns = document.createElement('noscript');
      ns.id = 'gtm-noscript';
      ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${s.gtm_id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.insertBefore(ns, document.body.firstChild);
    }
  }

  if (s.meta_pixel_id) {
    injectScript({ id: 'meta-pixel', inline:
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${s.meta_pixel_id}');fbq('track','PageView');` });
  }

  injectRawHtml(s.head_scripts, 'head');
  injectRawHtml(s.body_scripts, 'body');
}

// Apply favicon + SEO (title, description, keywords, Open Graph) from settings.
function applyBrowserChrome(settings) {
  if (!settings || typeof document === 'undefined') return;
  setFavicon(settings);
  applyTheme(settings);
  applyAnalytics(settings);

  if (settings.seo_title) document.title = settings.seo_title;
  setMeta('name', 'description', settings.seo_description);
  setMeta('name', 'keywords', settings.seo_keywords);

  setMeta('property', 'og:title', settings.seo_title);
  setMeta('property', 'og:description', settings.seo_description);
  setMeta('property', 'og:type', 'website');
  if (settings.og_image) setMeta('property', 'og:image', resolveAsset(settings.og_image));

  setMeta('name', 'twitter:card', settings.og_image ? 'summary_large_image' : 'summary');
  setMeta('name', 'twitter:title', settings.seo_title);
  setMeta('name', 'twitter:description', settings.seo_description);
  if (settings.og_image) setMeta('name', 'twitter:image', resolveAsset(settings.og_image));
}

export function useSiteSettings() {
  const [settings, setSettings] = useState(cache);

  useEffect(() => {
    let active = true;
    const notify = (data) => {
      if (active) setSettings(data);
    };
    subscribers.add(notify);

    if (cache) {
      notify(cache);
    } else if (!inflight) {
      inflight = api
        .get('/api/site-settings')
        .then((res) => {
          const rows = Array.isArray(res.data) ? res.data : [res.data];
          cache = rows.find((r) => r && r.is_active) || rows[0] || null;
          applyBrowserChrome(cache);
          subscribers.forEach((fn) => fn(cache));
        })
        .catch(() => {
          /* keep static fallbacks if the API is unreachable */
        });
    }

    return () => {
      active = false;
      subscribers.delete(notify);
    };
  }, []);

  return settings;
}
